/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";
import GetAllUsers from "../../../hooks/fetch-hooks/fetch-all/get-all-users";
import GetPatientMappingsByUserId from "../../../hooks/fetch-hooks/get-patient-mappings-by-userid/get-patient-mappings-by-userid";

/**
 * Step one: find who the quotation is for.
 *
 * Phone is the key the rest of the stack maps on, so it leads. The patient
 * mapping matters as much as the account — a quote raised against the wrong
 * family member is the expensive mistake here — so mappings are listed rather
 * than silently defaulted whenever there's more than one.
 */
const PatientStep = ({ onSelect }) => {
    const [phone, setPhone] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);

    const { data: users, isFetching } = GetAllUsers(phone, "contact_no");
    const { data: mappings } = GetPatientMappingsByUserId(selectedUser?.id || 0);

    const rows = users?.data?.data?.rows || [];
    const patients = mappings?.data?.data?.rows || [];
    const isSearchable = /^\d{10}$/.test(phone.trim());

    useEffect(() => {
        setSelectedUser(null);
    }, [phone]);

    return (
        <div className="mx-auto w-full max-w-xl">
            <label htmlFor="patient-phone" className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-cod-gray-400">
                Find the patient
            </label>

            <div className="relative mt-3">
                <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-cod-gray-300" size={18} />
                <input
                    id="patient-phone"
                    type="tel"
                    inputMode="numeric"
                    autoFocus
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="10-digit phone number"
                    className="w-full rounded-xl border-cod-gray-200 bg-white-50 py-3.5 pl-11 pr-4 text-base tracking-[0.02em] tabular-nums placeholder:tracking-normal placeholder:text-cod-gray-300 focus:border-elm-400 focus:ring-elm-400"
                />
            </div>

            {!isSearchable && (
                <p className="mt-2.5 text-xs text-cod-gray-400">
                    {phone.length ? `${10 - phone.length} more digits` : "Search starts at 10 digits."}
                </p>
            )}

            {isSearchable && isFetching && (
                <p className="mt-4 text-sm text-cod-gray-400">Searching…</p>
            )}

            {isSearchable && !isFetching && rows.length === 0 && (
                <div className="mt-4 rounded-xl border border-dashed border-cod-gray-200 p-6 text-center">
                    <p className="text-sm font-medium text-cod-gray-900">No account on this number</p>
                    <p className="mt-1 text-xs text-cod-gray-400">
                        Create the user first, then come back to raise the quotation.
                    </p>
                </div>
            )}

            {rows.length > 0 && (
                <ul className="mt-4 flex flex-col gap-2">
                    {rows.map((user) => {
                        const isSelected = selectedUser?.id === user.id;

                        return (
                            <li key={user.id}>
                                <button
                                    type="button"
                                    onClick={() => setSelectedUser(user)}
                                    aria-pressed={isSelected}
                                    className={`w-full rounded-xl border p-4 text-left transition-colors ${isSelected
                                        ? "border-elm-400 bg-elm-50"
                                        : "border-cod-gray-100 bg-white-50 hover:border-cod-gray-200"
                                        }`}
                                >
                                    <span className="block text-[15px] font-semibold text-cod-gray-900">
                                        {user.name || "Unnamed account"}
                                    </span>
                                    <span className="mt-0.5 block text-xs tabular-nums text-cod-gray-400">
                                        {user.contact_no}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {selectedUser && (
                <div className="mt-6 border-t border-cod-gray-100 pt-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cod-gray-400">
                        Who is it for
                    </p>

                    {patients.length === 0 ? (
                        <p className="mt-3 text-sm text-cod-gray-400">Loading patients on this account…</p>
                    ) : (
                        <ul className="mt-3 flex flex-col gap-2">
                            {patients.map((mapping) => (
                                <li key={mapping.id}>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onSelect({
                                                user: selectedUser,
                                                userMappingId: mapping.id,
                                                patientName: mapping.Patient?.name || selectedUser.name,
                                                relationship: mapping.relationship,
                                            })
                                        }
                                        className="flex w-full items-center justify-between rounded-xl border border-cod-gray-100 bg-white-50 p-4 text-left transition-colors hover:border-elm-400 hover:bg-elm-50"
                                    >
                                        <span>
                                            <span className="block text-[15px] font-semibold text-cod-gray-900">
                                                {mapping.Patient?.name || "Unnamed patient"}
                                            </span>
                                            <span className="mt-0.5 block text-xs capitalize text-cod-gray-400">
                                                {(mapping.relationship || "self").toLowerCase()}
                                            </span>
                                        </span>
                                        <span className="text-sm font-semibold text-elm-600">Continue</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default PatientStep;
